import React from 'react'
import { Text } from './Text'
import { Icon } from './Icon'
import { TechTag } from '../styles/common.styles'
import styled from 'styled-components'
import { Skill } from '../data/skills'

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    background: rgba(255, 255, 255, 0.08);
  }
`

const SkillHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const SkillNameWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const SkillLevel = styled.div<{ $level: Skill['level'] }>`
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.9rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.$level) {
      case 'Expert':
        return 'rgba(78, 205, 196, 0.1)'
      case 'Advanced':
        return 'rgba(255, 171, 0, 0.1)'
      case 'Intermediate':
        return 'rgba(255, 107, 107, 0.1)'
      default:
        return 'rgba(255, 255, 255, 0.1)'
    }
  }};
  color: ${(props) => {
    switch (props.$level) {
      case 'Expert':
        return '#4ECDC4'
      case 'Advanced':
        return '#FFAB00'
      case 'Intermediate':
        return '#ff6b6b'
      default:
        return '#ffffff'
    }
  }};
`

const ExperienceBadge = styled.div`
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &::after {
    content: 'years';
    font-size: 0.8rem;
    opacity: 0.7;
  }
`

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`

// Optional: color map for icons
const skillIconColorMap: Record<string, string> = {
  javascript: '#f7df1e',
  typescript: '#3178c6',
  react: '#61dafb',
  node: '#3c873a',
  mongodb: '#47a248',
  aws: '#ff9900',
  git: '#f34f29',
  docker: '#2496ed',
  graphql: '#e535ab',
}

interface SkillCardProps {
  skill: {
    name: string
    icon: Skill['icon']
    level: Skill['level']
    yearsOfExperience: number
    description: string
    technologies: string[]
  }
}

export const SkillCard: React.FC<SkillCardProps> = ({
  skill: { name, icon, level, yearsOfExperience, description, technologies },
}) => {
  return (
    <Card>
      <SkillHeader>
        <SkillNameWithIcon>
          <Icon icon={icon} size={32} color='#4ECDC4' />
          <Text
            text={name}
            fontSize='1.5rem'
            fontWeight='bold'
            color='#ffffff'
            margin='0'
            role='heading'
            aria-level={3}
            isResponsive={true}
          />
        </SkillNameWithIcon>
        <SkillLevel $level={level}>{level}</SkillLevel>
      </SkillHeader>

      <ExperienceBadge>{yearsOfExperience}</ExperienceBadge>

      <Text
        text={description}
        fontSize='1rem'
        color='#a0a0a0'
        margin='0'
        role='doc-subtitle'
        isResponsive={true}
        lineHeight='1.6'
      />

      <TechStack>
        {technologies.map((tech, i) => (
          <TechTag key={i}>{tech}</TechTag>
        ))}
      </TechStack>
    </Card>
  )
}
